import api from '../../../../api/client';

export const TYPE_OPTIONS = [
  { value: 'FullDay', label: 'FullDay' },
  { value: 'First Half', label: 'First Half' },
  { value: 'Second Half', label: 'Second Half' },
];

export const STATUS_OPTIONS = [
  { value: 'All', label: 'All Status' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
];

export const SEARCH_FIELD_OPTIONS = [
  { value: 'Name', label: 'Employee Name / Code' },
];

function mapBackendToFrontend(item) {
  if (!item) return null;

  const lDate = item.leave_date || item.LeaveDate || '';
  const dateStr = typeof lDate === 'string' ? lDate.split('T')[0] : '';
  const aDate = item.apply_date || item.ApplyDate || '';
  const applyDateStr = typeof aDate === 'string' ? aDate.split('T')[0] : '';

  const daysType = item.days_type ?? item.DaysType ?? 0;
  const daysTypeName = item.days_type_name || (daysType === 1 ? 'First Half' : daysType === 2 ? 'Second Half' : 'FullDay');

  return {
    id: item.id || item.ID,
    emp_id: item.emp_id || item.EmpId,
    employee: item.employee_name || item.employee || `Employee #${item.emp_id || item.EmpId}`,
    employeeId: item.employee_code || item.EmpCode || `EMP-${item.emp_id || item.EmpId}`,
    emp_name: item.employee_name || item.employee || '',
    emp_code: item.employee_code || item.EmpCode || '',
    leaveTypeId: item.leave_type_id || item.LeaveTypeID,
    leaveType: item.leave_type_name || 'General Leave',
    leaveTypeAlias: item.leave_type_alias || '',
    leaveTypeColor: item.leave_type_color || '#3B82F6',
    reasonId: item.reason_id || item.ReasonId,
    reason: item.reason_name || 'General',
    applyDate: applyDateStr,
    applyDateDisplay: applyDateStr ? applyDateStr.split('-').reverse().join('/') : '',
    fromDate: dateStr,
    tillDate: dateStr,
    leaveDateDisplay: dateStr ? dateStr.split('-').reverse().join('/') : '',
    days: Number(item.quantity ?? item.Quantity ?? 1),
    type: daysTypeName,
    daysType: daysType,
    remarks: item.remarks || item.Remarks || '',
    isApproved: item.is_approved ?? item.IsApproved ?? 0,
    status: item.status || (item.is_approved === 1 ? 'Approved' : item.is_approved === 2 ? 'Rejected' : 'Pending'),
    approvedBy: item.approved_by || item.ApprovedBy,
    approvedOn: item.approved_on || item.ApprovedOn,
  };
}

export const leaveApplicationApi = {
  getApplications: async (filters = {}) => {
    const params = {
      search: filters.search || filters.searchText || undefined,
      status_filter: filters.status || filters.statusFilter || 'Pending',
      employee_id: filters.employeeId && filters.employeeId !== 'All' ? Number(filters.employeeId) : undefined,
      leave_type_id: filters.leaveTypeId && filters.leaveTypeId !== 'All' ? Number(filters.leaveTypeId) : undefined,
      from_date: filters.fromDate || undefined,
      till_date: filters.toDate || filters.tillDate || undefined,
      page: filters.page || 1,
      page_size: filters.pageSize || 10,
    };

    const { data } = await api.get('/payroll/leave-application/', { params });
    const rawItems = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
    return {
      items: rawItems.map(mapBackendToFrontend).filter(Boolean),
      total_count: data?.total_count ?? rawItems.length,
      total_pages: data?.total_pages ?? 1,
      page: data?.page ?? (filters.page || 1),
      page_size: data?.page_size ?? (filters.pageSize || 10),
    };
  },

  getApplicationById: async (id) => {
    const { data } = await api.get(`/payroll/leave-application/${id}`);
    return mapBackendToFrontend(data);
  },

  getOptions: async () => {
    const { data } = await api.get('/payroll/leave-application/options');
    return {
      employees: Array.isArray(data?.employees)
        ? data.employees.map((e) => ({
            value: String(e.id),
            label: `${e.name} (${e.code})`,
            id: e.id,
            name: e.name,
            code: e.code,
          }))
        : [],
      leaveTypes: Array.isArray(data?.leave_types)
        ? data.leave_types.map((t) => ({
            value: String(t.id),
            label: t.alias ? `${t.name} (${t.alias})` : t.name,
            id: t.id,
            name: t.name,
            alias: t.alias,
            color: t.color,
          }))
        : [],
      reasons: Array.isArray(data?.reasons)
        ? data.reasons.map((r) => ({
            value: String(r.id),
            label: r.name,
            id: r.id,
            name: r.name,
          }))
        : [],
    };
  },

  getEmployeeBalances: async (empId) => {
    if (!empId) return [];
    try {
      const { data } = await api.get(`/payroll/leave-application/balances/${empId}`);
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  },

  createApplication: async (payload) => {
    const daysType = payload.type === 'First Half' ? 1 : payload.type === 'Second Half' ? 2 : 0;
    const body = {
      EmpId: Number(payload.emp_id || payload.employeeId),
      LeaveTypeID: Number(payload.leaveTypeId),
      LeaveDate: payload.fromDate || payload.leaveDate || new Date().toISOString().split('T')[0],
      FromDate: payload.fromDate || undefined,
      TillDate: payload.tillDate || undefined,
      Quantity: Number(payload.days || 1),
      DaysType: daysType,
      ReasonId: payload.reasonId ? Number(payload.reasonId) : undefined,
      Remarks: payload.remarks || '',
      ApplyDate: payload.applyDate || new Date().toISOString().split('T')[0],
    };
    const { data } = await api.post('/payroll/leave-application/', body);
    return mapBackendToFrontend(data);
  },

  updateApplication: async (id, payload) => {
    const daysType = payload.type === 'First Half' ? 1 : payload.type === 'Second Half' ? 2 : 0;
    const body = {
      EmpId: payload.emp_id || payload.employeeId ? Number(payload.emp_id || payload.employeeId) : undefined,
      LeaveTypeID: payload.leaveTypeId ? Number(payload.leaveTypeId) : undefined,
      LeaveDate: payload.fromDate || payload.leaveDate || undefined,
      FromDate: payload.fromDate || undefined,
      TillDate: payload.tillDate || undefined,
      Quantity: payload.days !== undefined ? Number(payload.days) : undefined,
      DaysType: daysType,
      ReasonId: payload.reasonId ? Number(payload.reasonId) : undefined,
      Remarks: payload.remarks !== undefined ? payload.remarks : undefined,
      ApplyDate: payload.applyDate || undefined,
    };
    const { data } = await api.put(`/payroll/leave-application/${id}`, body);
    return mapBackendToFrontend(data);
  },

  approveApplication: async (id, isApproved, remarks = '') => {
    const { data } = await api.post(`/payroll/leave-application/${id}/approve`, {
      is_approved: isApproved,
      remarks,
    });
    return mapBackendToFrontend(data);
  },

  deleteApplication: async (id) => {
    const { data } = await api.delete(`/payroll/leave-application/${id}`);
    return data;
  },
};
